import { createApp } from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import helpJs from './utils/help.js';
import * as echarts from 'echarts';
import installElementPlus from './plugins/element';
import axios from 'axios';
import * as ElementPlusIconsVue from '@element-plus/icons';
import { ElMessage } from 'element-plus';

// 引入 图标样式
import './assets/css/icon.css';
import './assets/icon/iconfont.css';
import './assets/css/main.scss';
import './assets/css/element.css';
import './assets/css/color-dark.css';
import './assets/css/editorRes.css';

const app = createApp(App);
Object.keys(ElementPlusIconsVue).forEach(key => {
  app.component(key, ElementPlusIconsVue[key]);
});
// 自定义指令 v-limit -> 判断当前页面按钮权限
app.directive('limit', {
  mounted(el, binding) {
    try {
      const limitsStr = localStorage.getItem('limits');
      if (!limitsStr) {
        return;
      }
      let localData = JSON.parse(limitsStr);
      if (!localData || !Array.isArray(localData)) {
        return;
      }
      let limi = localData.some(item => {
        return item.name == binding.value;
      });
      if (!limi) {
        el.parentNode && el.parentNode.removeChild(el);
      }
    } catch (e) {
      console.error('解析权限数据失败:', e);
    }
  },
});

// 引入全局api
import cloudApi from './api/cloud';
import pmsApi from './api/pmsApi';
import msApi from './api/msApi';
import deviceApi from './api/deviceApi';

app.config.globalProperties.$helpJs = helpJs;
app.config.globalProperties.$pmsApi = pmsApi;
app.config.globalProperties.$msApi = msApi;
app.config.globalProperties.$cloudApi = cloudApi;
app.config.globalProperties.$deviceApi = deviceApi;
app.config.globalProperties.$echarts = echarts;
app.config.globalProperties.$axios = axios;
app.config.globalProperties.$icon = ElementPlusIconsVue;

// 统一的错误过滤函数 - 判断是否是应该忽略的浏览器已知错误
function shouldIgnoreError(error) {
  if (!error) return false;

  // 获取错误消息的多种形式
  let errorMessage = '';
  if (error.message) {
    errorMessage = error.message;
  } else if (typeof error === 'string') {
    errorMessage = error;
  } else if (error.toString && typeof error.toString === 'function') {
    errorMessage = error.toString();
  }

  // 转换为小写以便匹配
  const lowerMessage = errorMessage.toLowerCase();

  // ResizeObserver 相关错误（浏览器已知问题，不影响功能）
  const resizeObserverErrors = [
    'resizeobserver',
    'resizeobserver loop',
    'resizeobserver loop completed',
    'resizeobserver loop limit exceeded',
    'resizeobserver loop completed with undelivered notifications',
  ];

  // 检查是否匹配任何已知的应忽略的错误
  for (const ignorePattern of resizeObserverErrors) {
    if (lowerMessage.includes(ignorePattern.toLowerCase())) {
      return true;
    }
  }

  return false;
}

// 全局错误处理
app.config.errorHandler = (err, instance, info) => {
  // 使用统一的错误过滤函数
  if (shouldIgnoreError(err)) {
    return;
  }

  console.error('Vue 全局错误:', err);
  console.error('错误信息:', info);
  console.error('组件实例:', instance);

  // 格式化错误信息
  let errorMessage = '发生未知错误';
  if (err && err.message) {
    errorMessage = err.message;
  } else if (typeof err === 'string') {
    errorMessage = err;
  } else if (err && err.toString && err.toString() !== '[object Object]') {
    errorMessage = err.toString();
  } else if (err && err.response) {
    // axios 错误
    const response = err.response;
    if (response.data && response.data.msg) {
      errorMessage = response.data.msg;
    } else {
      errorMessage = `请求失败 (${response.status})`;
    }
  }

  // 显示错误提示
  ElMessage({
    showClose: true,
    message: errorMessage,
    type: 'error',
    duration: 5000,
  });
};

// 未捕获的 Promise 错误处理
window.addEventListener('unhandledrejection', event => {
  // 使用统一的错误过滤函数
  if (shouldIgnoreError(event.reason)) {
    event.preventDefault();
    return;
  }

  const reason = event.reason;
  console.error('未捕获的 Promise 错误:', reason);

  let errorMessage = '发生未知错误';

  if (reason && reason.msg) {
    errorMessage = reason.msg;
  } else if (reason && reason.message) {
    errorMessage = reason.message;
  } else if (typeof reason === 'string') {
    errorMessage = reason;
  } else if (reason && reason.toString && reason.toString() !== '[object Object]') {
    errorMessage = reason.toString();
  }

  ElMessage({
    showClose: true,
    message: errorMessage,
    type: 'error',
    duration: 5000,
  });

  // 阻止默认的错误处理（可选）
  // event.preventDefault();
});

// 捕获全局错误，过滤已知的浏览器警告错误
window.addEventListener(
  'error',
  event => {
    // 使用统一的错误过滤函数
    if (shouldIgnoreError(event.error || event.message)) {
      event.preventDefault();
      return;
    }
    // 其他错误继续正常处理
  },
  true
);

installElementPlus(app);
app.use(store).use(router).mount('#app');
